import axios from 'axios';
import { Provider, Client, Visit, LoginCredentials, VisitCreate, VisitUpdate, Service } from '../types';
import { logger } from '../utils/logger';
import { runtimeConfig } from '../config/runtimeConfig';

// Get environment configuration from runtime config
const ENVIRONMENT = runtimeConfig.REACT_APP_ENVIRONMENT;
const API_BASE_URL = runtimeConfig.REACT_APP_API_URL;
const LOG_LEVEL = process.env.REACT_APP_LOG_LEVEL || 'debug';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
  },
});

// Log environment configuration
logger.info('Environment Configuration:', {
  environment: ENVIRONMENT,
  apiBaseUrl: `${API_BASE_URL}/api/v1`,
  logLevel: LOG_LEVEL
});

// Console log for debugging
console.log('Provider Portal - API Base URL being used:', API_BASE_URL);
console.log('Provider Portal - Full API URL:', `${API_BASE_URL}/api/v1`);
console.log('Provider Portal - Environment:', ENVIRONMENT);

// Test API connection
export const testAPI = async () => {
  try {
    logger.info('Testing API connection...');
    const response = await api.get('/');
    logger.info('API root response:', response.data);
    return response.data;
  } catch (error: any) {
    logger.error('API test failed:', error);
    logger.error('Status:', error.response?.status);
    logger.error('Data:', error.response?.data);
    return null;
  }
};

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Console log for debugging
  console.log('Provider Portal - Making API request to:', config.url);
  console.log('Provider Portal - Full URL:', (config.baseURL || '') + (config.url || ''));
  console.log('Provider Portal - Method:', config.method);
  
  // Log API request
  logger.logApiRequest(
    config.method?.toUpperCase() || 'UNKNOWN',
    config.url || 'UNKNOWN',
    config.data,
    config.headers
  );
  
  return config;
});

// Handle response errors - automatically logout on 401
api.interceptors.response.use(
  (response) => {
    // Log successful API response
    logger.logApiResponse(
      response.config.method?.toUpperCase() || 'UNKNOWN',
      response.config.url || 'UNKNOWN',
      response.status,
      response.data
    );
    
    return response;
  },
  (error) => {
    // Log API error
    if (error.response) {
      logger.logApiResponse(
        error.config?.method?.toUpperCase() || 'UNKNOWN',
        error.config?.url || 'UNKNOWN',
        error.response.status,
        error.response.data
      );
    } else {
      logger.logApiError(
        error.config?.method?.toUpperCase() || 'UNKNOWN',
        error.config?.url || 'UNKNOWN',
        error
      );
    }
    
    if (error.response?.status === 401) {
      logger.warn('Session expired, redirecting to login...');
      // Clear auth token
      localStorage.removeItem('authToken');
      localStorage.removeItem('token_expires_at');
      // Redirect to login page
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (credentials: LoginCredentials): Promise<any> => {
    logger.info('Attempting login to:', `${API_BASE_URL}/auth/login`);
    logger.debug('Credentials:', credentials);
    try {
      const response = await api.post('/auth/login', credentials);
      logger.info('Login response data:', response.data);
      logger.debug('Login response data type:', typeof response.data);
      logger.debug('Login response data keys:', Object.keys(response.data));
      return response.data;
    } catch (error: any) {
      logger.error('Login error:', error);
      logger.error('Error response:', error.response);
      logger.error('Error status:', error.response?.status);
      logger.error('Error data:', error.response?.data);
      throw error;
    }
  },

  getCurrentProvider: async (): Promise<Provider> => {
    const response = await api.get('/auth/me');
    console.log('API: getCurrentProvider response:', response.data);
    console.log('API: getCurrentProvider response keys:', Object.keys(response.data));
    return response.data;
  },

  refreshToken: async (): Promise<{ access_token: string; expiration?: number }> => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No authenticated user');
    }

    // Use fetch so a failed refresh does not trigger the axios 401 logout interceptor mid-call
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const tokenData = await response.json();
    localStorage.setItem('authToken', tokenData.access_token);
    if (tokenData.expiration) {
      localStorage.setItem(
        'token_expires_at',
        new Date(Date.now() + tokenData.expiration * 1000).toISOString()
      );
    }
    return tokenData;
  },
};

export async function requestPasswordReset(email: string): Promise<string> {
  const response = await api.post('/auth/forgot-password', { user_email: email });
  return response.data.message;
}

export async function confirmPasswordReset(token: string, newPassword: string): Promise<string> {
  const response = await api.post('/auth/reset-password', {
    token,
    new_password: newPassword,
  });
  return response.data.message;
}

// Provider API
export const providerAPI = {
  getProvider: async (providerId: number): Promise<any> => {
    console.log('providerAPI.getProvider: providerId:', providerId);
    const response = await api.get(`/providers/${providerId}`);
    console.log('providerAPI.getProvider: response.data:', response.data);
    return response.data;
  },
};

// Client API
export const clientAPI = {
  getClients: async (providerId?: number): Promise<Client[]> => {
    // Get the current provider's clients
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No authentication token');
    }
    
    // Always require providerId for provider portal security
    if (!providerId) {
      throw new Error('Provider ID is required to fetch clients');
    }
    
    // Use the provider-specific endpoint
    const url = `/clients/provider/${providerId}?limit=1000`;
    console.log('clientAPI.getClients: providerId:', providerId);
    console.log('clientAPI.getClients: URL:', url);
    const response = await api.get(url);
    console.log('clientAPI.getClients: response.data:', response.data);
    console.log('clientAPI.getClients: response.data.length:', response.data.length);
    return response.data;
  },
  
  getClient: async (id: string): Promise<Client> => {
    const response = await api.get(`/clients/${id}`);
    return response.data;
  },

  getAuthorizedServices: async (clientId: number, providerId: number): Promise<Service[]> => {
    try {
      console.log('clientAPI.getAuthorizedServices: clientId:', clientId, 'type:', typeof clientId);
      console.log('clientAPI.getAuthorizedServices: providerId:', providerId, 'type:', typeof providerId);
      
      // Use the correct RESTful endpoint that returns ServiceResponse objects
      const url = `/client-isp-services/client/${clientId}/provider/${providerId}/services`;
      console.log('clientAPI.getAuthorizedServices: URL:', url);
      
      const response = await api.get(url);
      console.log('clientAPI.getAuthorizedServices: response:', response);
      console.log('clientAPI.getAuthorizedServices: response.data:', response.data);
      console.log('clientAPI.getAuthorizedServices: response.data.length:', Array.isArray(response.data) ? response.data.length : 'Not an array');
      
      return response.data || [];
    } catch (error) {
      console.error('clientAPI.getAuthorizedServices: Error:', error);
      return [];
    }
  },

  getAuthorizedTransportationServices: async (clientId: number, providerId: number): Promise<Service[]> => {
    try {
      console.log('clientAPI.getAuthorizedTransportationServices: clientId:', clientId, 'type:', typeof clientId);
      console.log('clientAPI.getAuthorizedTransportationServices: providerId:', providerId, 'type:', typeof providerId);
      
      // Use the correct RESTful endpoint that returns transportation services
      const url = `/client-isp-services/client/${clientId}/provider/${providerId}/transportation`;
      console.log('clientAPI.getAuthorizedTransportationServices: URL:', url);
      
      const response = await api.get(url);
      console.log('clientAPI.getAuthorizedTransportationServices: response:', response);
      console.log('clientAPI.getAuthorizedTransportationServices: response.data:', response.data);
      console.log('clientAPI.getAuthorizedTransportationServices: response.data.length:', Array.isArray(response.data) ? response.data.length : 'Not an array');
      
      return response.data || [];
    } catch (error) {
      console.error('clientAPI.getAuthorizedTransportationServices: Error:', error);
      return [];
    }
  },

  /** Client IDs with at least one active ISP service linked to a service whose title contains "Transportation". */
  getClientIdsWithTransportationIsp: async (providerId: number): Promise<number[]> => {
    const response = await api.get<number[]>(
      `/client-isp-services/provider/${providerId}/clients-with-transportation`
    );
    const data = response.data;
    return Array.isArray(data) ? data.map(Number) : [];
  },
};

// Service API
export const serviceAPI = {
  getServices: async (): Promise<Service[]> => {
    const response = await api.get('/services');
    return response.data;
  },
  
  getService: async (id: number): Promise<Service> => {
    const response = await api.get(`/services/${id}`);
    return response.data;
  },
};

// Transportation API
export const transportationAPI = {
  getDimensions: async () => {
    const response = await api.get('/transportation/dimensions');
    return response.data;
  },
  
  list: async () => {
    try {
      const response = await api.get('/transportation');
      return response.data;
    } catch (error) {
      console.error('Transportation list error:', error);
      throw error;
    }
  },

  create: async (data: any) => {
    try {
      const response = await api.post('/transportation', data);
      return response.data;
    } catch (error) {
      console.error('Transportation create error:', error);
      throw error;
    }
  },

  update: async (id: number, data: any) => {
    try {
      const response = await api.put(`/transportation/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Transportation update error:', error);
      throw error;
    }
  },

  delete: async (id: number) => {
    try {
      const response = await api.delete(`/transportation/${id}`);
      return response.data;
    } catch (error) {
      console.error('Transportation delete error:', error);
      throw error;
    }
  }
};

// Client ISP API
export const clientISPAPI = {
  list: async (clientId: number, activeStatus?: boolean) => {
    try {
      const params: any = {};
      if (activeStatus !== undefined) params.active_status = activeStatus.toString();
      const response = await api.get(`/client-isp/client/${clientId}`, { params });
      return response.data;
    } catch (error) {
      console.error('Client ISP list error:', error);
      throw error;
    }
  },

  /**
   * Get ISPs for visit entry, filtered by provider, client, and visit date.
   * Returns only active ISPs where visit_date falls between span_start_date and span_end_date.
   */
  listForVisit: async (providerId: number, clientId: number, visitDate?: string | null) => {
    try {
      const params: Record<string, string> = {
        provider_id: providerId.toString(),
        client_id: clientId.toString(),
      };
      if (visitDate) {
        const datePart = visitDate.split('T')[0];
        if (datePart) params.visit_date = datePart;
      }
      const response = await api.get('/client-isp/for-visit', { params });
      return response.data;
    } catch (error) {
      console.error('Client ISP listForVisit error:', error);
      throw error;
    }
  },

  get: async (ispId: number) => {
    try {
      const response = await api.get(`/client-isp/${ispId}`);
      return response.data;
    } catch (error) {
      console.error('Client ISP get error:', error);
      throw error;
    }
  },

  outcomes: {
    list: async (ispId: number, activeStatus?: boolean) => {
      try {
        const params: Record<string, string> = {};
        if (activeStatus !== undefined) params.active_status = activeStatus.toString();
        const response = await api.get(`/client-isp/${ispId}/outcomes`, { params });
        return response.data;
      } catch (error) {
        console.error('Client ISP Outcomes list error:', error);
        throw error;
      }
    }
  },

  experiences: {
    list: async (ispId: number, activeStatus?: boolean) => {
      try {
        const params: Record<string, string> = {};
        if (activeStatus !== undefined) params.active_status = activeStatus.toString();
        const response = await api.get(`/client-isp/${ispId}/experiences`, { params });
        return response.data;
      } catch (error) {
        console.error('Client ISP Experiences list error:', error);
        throw error;
      }
    }
  },

  supports: {
    list: async (ispId: number, activeStatus?: boolean) => {
      try {
        const params: Record<string, string> = {};
        if (activeStatus !== undefined) params.active_status = activeStatus.toString();
        const response = await api.get(`/client-isp/${ispId}/supports`, { params });
        return response.data;
      } catch (error) {
        console.error('Client ISP Supports list error:', error);
        throw error;
      }
    }
  },

  /** Get client_isp_services for an ISP (used to filter supports by selected service) */
  getServicesByIsp: async (ispId: number) => {
    try {
      const response = await api.get(`/client-isp-services/isp/${ispId}`);
      return response.data || [];
    } catch (error) {
      console.error('Client ISP Services by ISP error:', error);
      return [];
    }
  }
};

// Outcome API (deprecated - use clientISPAPI.getForVisit instead)
export const outcomeAPI = {
  getOutcomes: async () => {
    const response = await api.get('/outcomes');
    return response.data;
  },
  
  getOutcomesByClient: async (clientId: number | string) => {
    const response = await api.get(`/outcomes/client/${clientId}`);
    return response.data;
  },
};

// Experience API (deprecated - use clientISPAPI.getForVisit instead)
export const experienceAPI = {
  getExperiencesByOutcome: async (outcomeId: number) => {
    const response = await api.get(`/experiences/outcome/${outcomeId}`);
    return response.data;
  },
};

// Unusual Incident API
export const unusualIncidentAPI = {
  getTypes: async () => {
    const response = await api.get('/unusual-incidents');
    return response.data;
  },
};

// Location API
export const locationAPI = {
  list: async (): Promise<any[]> => {
    const response = await api.get('/locations');
    return response.data;
  },
  listByProvider: async (providerId: number): Promise<any[]> => {
    const response = await api.get(`/locations/by-provider/${providerId}`);
    return response.data;
  },
  get: async (id: number | string) => {
    const response = await api.get(`/locations/${id}`);
    return response.data;
  },
  create: async (locationData: any) => {
    const response = await api.post('/locations', locationData);
    return response.data;
  },
};

// Address validation / reverse geocode (Google Maps via API)
export const addressAPI = {
  validate: async (data: {
    address_line1?: string;
    address_line2?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    address_lines?: string[];
  }) => {
    const response = await api.post('/addresses/validate', data);
    return response.data;
  },
  reverseGeocode: async (data: { latitude: number; longitude: number }) => {
    const response = await api.post('/addresses/reverse-geocode', data);
    return response.data;
  },
};

// Billing API
export const billingAPI = {
  getProviderBilling: async (providerId: number, startDate?: string, endDate?: string) => {
    try {
      const params: any = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const response = await api.get(`/billing/${providerId}`, { params });
      return response.data;
    } catch (error) {
      console.error('Billing error:', error);
      throw error;
    }
  },

  getProviderBillingSummary: async (providerId: number, startDate?: string, endDate?: string) => {
    try {
      const params: any = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const response = await api.get(`/billing/${providerId}/summary`, { params });
      return response.data;
    } catch (error) {
      console.error('Billing summary error:', error);
      throw error;
    }
  },

  getProviderBillingSummaryDetails: async (providerId: number, startDate?: string, endDate?: string, clientId?: number, serviceCode?: string) => {
    try {
      const params: any = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (clientId) params.client_id = clientId;
      if (serviceCode) params.service_code = serviceCode;
      const response = await api.get(`/billing/${providerId}/summary-details`, { params });
      return response.data;
    } catch (error) {
      console.error('Billing summary error:', error);
      throw error;
    }
  },

  exportProviderBilling: async (providerId: number, startDate?: string, endDate?: string, format: string = 'txt') => {
    try {
      const params: any = { format };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const response = await api.get(`/billing/${providerId}/export`, { params });
      return response.data;
    } catch (error) {
      console.error('Billing export error:', error);
      throw error;
    }
  }
};

export const visitParentAPI = {
  list: async (
    providerId: number,
    limit: number = 10,
    skip: number = 0,
    filters?: {
      startDate?: string;
      endDate?: string;
      clientId?: number;
      status?: string;
    }
  ) => {
    const params = new URLSearchParams({
      skip: String(skip),
      limit: String(limit),
      provider_id: String(providerId),
    });
    if (filters?.startDate) params.append('start_date', filters.startDate);
    if (filters?.endDate) params.append('end_date', filters.endDate);
    if (filters?.clientId != null) params.append('client_id', String(filters.clientId));
    if (filters?.status) params.append('status', filters.status);
    const response = await api.get(`/visit-parents?${params.toString()}`);
    return response.data;
  },
  delete: async (visitParentId: number) => {
    await api.delete(`/visit-parents/${visitParentId}`);
  },
  create: async (data: { provider_id: number }) => {
    const response = await api.post('/visit-parents', data);
    return response.data;
  },
  get: async (visitParentId: number) => {
    const response = await api.get(`/visit-parents/${visitParentId}`);
    return response.data;
  },
};

export const transportationParentAPI = {
  list: async (
    providerId: number,
    limit: number = 100,
    skip: number = 0,
    filters?: {
      startDate?: string;
      endDate?: string;
      clientId?: number;
    }
  ) => {
    const params = new URLSearchParams({
      skip: String(skip),
      limit: String(limit),
      provider_id: String(providerId),
    });
    if (filters?.startDate) params.append('start_date', filters.startDate);
    if (filters?.endDate) params.append('end_date', filters.endDate);
    if (filters?.clientId != null) params.append('client_id', String(filters.clientId));
    const response = await api.get(`/transportation-parents?${params.toString()}`);
    return response.data;
  },
  create: async (data: { provider_id: number }) => {
    const response = await api.post('/transportation-parents', data);
    return response.data;
  },
  get: async (transportationParentId: number) => {
    const response = await api.get(`/transportation-parents/${transportationParentId}`);
    return response.data;
  },
  delete: async (transportationParentId: number) => {
    await api.delete(`/transportation-parents/${transportationParentId}`);
  },
};

// Visit API
export const visitAPI = {
  getVisits: async (
    providerId?: number, 
    limit: number = 10, 
    skip: number = 0,
    filters?: {
      startDate?: string;
      endDate?: string;
      clientId?: number;
      status?: string;
    }
  ): Promise<Visit[] | { items: Visit[], total: number }> => {
    // Always require providerId for provider portal security
    if (!providerId) {
      throw new Error('Provider ID is required to fetch visits');
    }
    
    // Build query parameters
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString()
    });
    
    // Add filter parameters if provided
    if (filters?.startDate) {
      params.append('start_date', filters.startDate);
    }
    if (filters?.endDate) {
      params.append('end_date', filters.endDate);
    }
    if (filters?.clientId) {
      params.append('client_id', filters.clientId.toString());
    }
    if (filters?.status) {
      params.append('status', filters.status);
    }
    
    // Use the provider-specific endpoint with pagination and filters
    const url = `/visits/provider/${providerId}?${params.toString()}`;
    console.log('visitAPI.getVisits: providerId:', providerId, 'limit:', limit, 'skip:', skip, 'filters:', filters);
    console.log('visitAPI.getVisits: URL:', url);
    const response = await api.get(url);
    console.log('visitAPI.getVisits: response.data:', response.data);
    
    // Handle both array response and paginated response
    if (Array.isArray(response.data)) {
      return response.data;
    } else {
      return response.data;
    }
  },
  
  getVisit: async (id: string): Promise<Visit> => {
    const response = await api.get(`/visits/${id}`);
    return response.data;
  },
  
  createVisit: async (visitData: VisitCreate): Promise<Visit> => {
    const response = await api.post('/visits', visitData);
    return response.data;
  },
  
  updateVisit: async (id: string, visitData: VisitUpdate): Promise<Visit> => {
    const response = await api.put(`/visits/${id}`, visitData);
    return response.data;
  },
  
  deleteVisit: async (id: string): Promise<void> => {
    await api.delete(`/visits/${id}`);
  },
  
  getWeeklyHours: async (providerId: number, scheduledStart: string): Promise<{ provider_id: number; week_key: string; total_hours: number }> => {
    if (!scheduledStart) {
      throw new Error('Scheduled start date is required');
    }
    
    console.log('getWeeklyHours - Raw input:', scheduledStart);
    
    // Convert date to the format expected by the backend: YYYY-MM-DD HH:MM:SS
    let formattedDate: string;
    
    // Handle datetime-local format (YYYY-MM-DDTHH:mm) or already formatted strings
    if (scheduledStart.includes('T')) {
      // datetime-local format: "YYYY-MM-DDTHH:mm" -> convert to "YYYY-MM-DD HH:MM:SS"
      const [datePart, timePart] = scheduledStart.split('T');
      let timeWithSeconds: string;
      if (timePart) {
        // Check if seconds are already present (HH:mm:ss)
        const timeParts = timePart.split(':');
        if (timeParts.length === 2) {
          // Only hours and minutes, add seconds
          timeWithSeconds = `${timePart}:00`;
        } else if (timeParts.length === 3) {
          // Already has seconds
          timeWithSeconds = timePart;
        } else {
          timeWithSeconds = '00:00:00';
        }
      } else {
        timeWithSeconds = '00:00:00';
      }
      formattedDate = `${datePart} ${timeWithSeconds}`;
      console.log('getWeeklyHours - Converted from datetime-local format:', formattedDate);
    } else {
      // Try to parse as Date object and format it
      try {
        const date = new Date(scheduledStart);
        if (isNaN(date.getTime())) {
          throw new Error('Invalid date');
        }
        
        // Format as YYYY-MM-DD HH:MM:SS (using local time, not UTC)
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        
        formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        console.log('getWeeklyHours - Formatted from Date object:', formattedDate);
      } catch (error) {
        // If parsing fails, try to use the string as-is (might already be in correct format)
        formattedDate = scheduledStart.trim();
        console.log('getWeeklyHours - Using string as-is:', formattedDate);
      }
    }
    
    if (!formattedDate) {
      throw new Error('Scheduled start date cannot be empty');
    }
    
    console.log('getWeeklyHours - Final formatted date:', formattedDate);
    console.log('getWeeklyHours - Provider ID:', providerId);
    
    // Manually construct query string to ensure it's properly encoded
    const params = new URLSearchParams();
    params.append('scheduled_start', formattedDate);
    const queryString = params.toString();
    
    const url = `/visits/provider/${providerId}/weekly-hours?${queryString}`;
    console.log('getWeeklyHours - API URL:', url);
    
    const response = await api.get(url);
    console.log('getWeeklyHours - API response:', response.data);
    return response.data;
  },
};

// Unusual Incident (UI) API
export const uiAPI = {
  list: async (params: {
    provider_id?: number;
    client_id?: number;
    visit_id?: number;
    transportation_id?: number;
    mui?: boolean;
  }) => {
    const sp = new URLSearchParams();
    if (params.provider_id != null) sp.set('provider_id', String(params.provider_id));
    if (params.client_id != null) sp.set('client_id', String(params.client_id));
    if (params.visit_id != null) sp.set('visit_id', String(params.visit_id));
    if (params.transportation_id != null) sp.set('transportation_id', String(params.transportation_id));
    if (params.mui != null) sp.set('mui', String(params.mui));
    const qs = sp.toString();
    const response = await api.get(`/ui${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  create: async (data: import('../types').UiCreate) => {
    const response = await api.post('/ui', data);
    return response.data;
  },

  update: async (id: number, data: import('../types').UiUpdate) => {
    const response = await api.put(`/ui/${id}`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/ui/${id}`);
    return response.data;
  },
};

export const providerVehiclesAPI = {
  list: async (params?: { providerId?: number; includeInactive?: boolean }) => {
    const sp = new URLSearchParams();
    if (params?.providerId != null) sp.set('provider_id', String(params.providerId));
    if (params?.includeInactive) sp.set('include_inactive', 'true');
    const qs = sp.toString();
    const response = await api.get(`/provider-vehicles${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get(`/provider-vehicles/${id}`);
    return response.data;
  },

  create: async (data: import('../types').ProviderVehicleCreate) => {
    const response = await api.post('/provider-vehicles', data);
    return response.data;
  },

  update: async (id: number, data: import('../types').ProviderVehicleUpdate) => {
    const response = await api.put(`/provider-vehicles/${id}`, data);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/provider-vehicles/${id}`);
    return response.data;
  },
};

export interface ScheduleOverlapConflict {
  source: string;
  id: number;
  scheduled_start?: string;
  scheduled_end?: string;
}

export interface ScheduleOverlapResult {
  has_overlap: boolean;
  conflicts: ScheduleOverlapConflict[];
}

export const scheduleOverlapAPI = {
  check: async (params: {
    client_id: number;
    start: string;
    end: string;
    provider_id?: number;
    exclude_visit_id?: number;
    exclude_transportation_id?: number;
  }): Promise<ScheduleOverlapResult> => {
    const sp = new URLSearchParams();
    sp.set('client_id', String(params.client_id));
    sp.set('start', params.start);
    sp.set('end', params.end);
    if (params.provider_id != null) sp.set('provider_id', String(params.provider_id));
    if (params.exclude_visit_id != null) sp.set('exclude_visit_id', String(params.exclude_visit_id));
    if (params.exclude_transportation_id != null) {
      sp.set('exclude_transportation_id', String(params.exclude_transportation_id));
    }
    const { data } = await api.get<ScheduleOverlapResult>(`/schedule-overlap/check?${sp.toString()}`);
    return data;
  },
};

export default api;
